/**
 * Brand context — the seam for Decision #21 (fully white-label reseller
 * experience). Every customer-facing surface should read its name/logo/colors/
 * support contacts from here, never from a hardcoded "Possignin"/"ITCity"
 * literal. Default is the platform brand; reseller shells wrap their subtree
 * with the reseller's brand so the same components render white-labelled.
 *
 * Wiring this with the default changes nothing visually — it's the foundation
 * the 198-literal sweep (PROJECT_ASSESSMENT.md §4F) migrates onto.
 */
import { createContext, useContext, useLayoutEffect, type ReactNode } from 'react';

export type Brand = {
  /** Full display name, e.g. "Possignin" or a reseller's "Acme POS". */
  name: string;
  /** Short label for tight spaces. */
  shortName: string;
  /** Primary accent (hex). */
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
  /** Optional logo asset URL; null → use the geometric <Logo/> mark. */
  logoUrl: string | null;
  /**
   * Apex domain for customer-facing URLs (subdomains, status page, CNAME target).
   * Platform → "itcitysoft.com"; a white-label reseller → their own, e.g. "acmepos.com".
   * Lets the same chrome render `<sub>.{brand.domain}` without leaking ITCity on a
   * reseller's customer path (Decision #21 / SECURITY_COMPLIANCE_CHECKLIST §22).
   */
  domain: string;
  /**
   * True for the ITCity/Possignin platform host; false for a white-label reseller
   * host. Drives host-aware routing (ITCity company page vs. reseller landing) and
   * lets components keep ITCity copy only when this is the platform.
   */
  isPlatform: boolean;
};

export const PLATFORM_BRAND: Brand = {
  name: 'Possignin',
  shortName: 'Possignin',
  primaryColor: '#4F46E5',
  supportEmail: 'support@itcitysoft.com',
  supportPhone: null,
  logoUrl: null,
  domain: 'itcitysoft.com',
  isPlatform: true,
};

const BrandContext = createContext<Brand>(PLATFORM_BRAND);

export function BrandProvider({ brand = PLATFORM_BRAND, children }: { brand?: Brand; children: ReactNode }) {
  // Drive the brand accent from the resolved brand. The Tailwind `bg-brand` /
  // `text-brand` utilities read `--color-brand`, so overriding it on <html> makes
  // every accent follow the reseller's primaryColor (Decision #21). Platform
  // brand keeps the default indigo, so the platform host is unchanged.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (brand.isPlatform) {
      root.style.removeProperty('--color-brand');
    } else {
      root.style.setProperty('--color-brand', brand.primaryColor);
    }
  }, [brand.isPlatform, brand.primaryColor]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

/** Read the active brand. Defaults to the platform brand outside any provider. */
export function useBrand(): Brand {
  return useContext(BrandContext);
}
